/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-continue */
/* eslint-disable max-len */

/**
 * User-friendly permission labels mapping
 * Maps technical permission keys to human-readable labels
 */

export interface PermissionLabelMap {
  [key: string]: string | PermissionLabelMap;
}

export const permissionLabels: PermissionLabelMap = {
  // Top-level permissions
  dashboard: "Dashboard",
  notifications: "Notifications",
  holidays: "Holidays",
  adminSettings: "Admin Settings",
  superAdminPanel: "Super Admin Panel",
  view_audit_logs: "View Audit Logs",

  // Attendance permissions
  attendance: {
    _label: "Attendance",
    admin: "Admin Attendance",
    employee: "Employee Attendance",
    viewAll: "View All Attendance",
    viewSelf: "View Self Attendance",
  },

  // Employees permissions
  employees: {
    _label: "Employees",
    department: "Department",
    leaveEmployees: "Leave Employees",
    adminLeave: "Admin Leave",
    leaveConfiguration: "Leave Configuration",
    allEmployees: "All Employees Navbar",
    viewProfile: "View Employee Profile",
    addleave: "Add Leave",
    employeeleaveEdit: "Edit Employee Leave",
    adminleaveReject: "Reject Leave (Admin)",
    adminLeaveApproved: "Approve Leave (Admin)",
    userBalance: "User Balance",
    balanceSummaryDownload: "Balance Summary Download",
  },

  // Reports permissions
  reports: {
    _label: "Reports",
    leaveReports: "Leave Reports",
    attendanceReports: "Attendance Reports",
    leaveHistoryReport: "Leave History Report",
    leaveReportDownload: "Download Leave Reports",
    attendancereportDownload: "Download Attendance Reports",
    leaveBalanceReportExport: "Export Leave Balance Report",
    leaveTemplate: "Leave: Download Import Template",
    leaveImport: "Leave: Import Report",
    attendanceTemplate: "Attendance: Download Import Template",
    attendanceImport: "Attendance: Import Report",
    balanceTemplate: "Balance: Download Import Template",
    balanceImport: "Balance: Import Report",
    balanceApprove: "Balance: Approve Imported Balances",
  },

  // Organization Management permissions
  organization_management: {
    _label: "Organization Management",
    memberAdd: "Add Members",
    organizationUpdate: "Update Organization",
  },

  // Department permissions
  department: {
    _label: "Department",
    view: "View Departments",
    add: "Add Departments",
    update: "Update Departments",
    delete: "Delete Departments",
  },

  // Permission Management permissions
  permission_management: {
    _label: "Permission Management",
    view_user_permissions: "View User Permissions",
    edit_users_permissions: "Edit User Permissions",
  },

  // Role Management permissions
  role_management: {
    _label: "Role Management",
    create_role: "Create Roles",
  },
  // Ticket Management permissions
  ticket: {
    _label: "Issue Ticket Management",
    view: "View Issues Ticket",
    create: "Create Issues Ticket",
  },

  // Employment History permissions
  employment_history: {
    _label: "Employment History",
    add: "Add Employment History",
    update: "Update Employment History",
    delete: "Delete Employment History",
  },
configuration_management: {
    _label: "Configuration Management",
    user_configrations_hours: "User Configrations Hours",
    department_configrations_hours: "Department Configrations Hours",
    program_configrations: "Program Configrations",
  },
  // Program Management permissions
  program_management: {
    _label: "Program Management",
    create: "Create Programs",
    update: "Update Programs",
    delete: "Delete Programs",
  },

  // User Profile permissions
  userProfile: {
    _label: "User Profile",
    update_profile: "Update Profile",
    view_profile: "View Profile",
  },

  // Holiday Management permissions
  holiday_managment: {
    _label: "Holiday Management",
    create: "Create Holidays",
    update: "Update Holidays",
    delete: "Delete Holidays",
  },
};

/**
 * Get user-friendly label for a permission key
 * @param key - Permission key (e.g., "userProfile", "attendance.admin", "connect.meeting_management.view")
 * @returns User-friendly label (e.g., "User Profile", "Admin Attendance", "View Meetings")
 */
export const getPermissionLabel = (key: string): string => {
  if (!key) return "";

  // Handle nested keys like "attendance.admin" or "connect.meeting_management.view"
  const parts = key.split(".");
  let current: unknown = permissionLabels;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    if (!current || typeof current !== "object") {
      // Fallback: convert camelCase/snake_case to Title Case
      return formatKeyToLabel(key);
    }

    const curRec = current as Record<string, unknown>;
    if (curRec[part]) {
      if (typeof curRec[part] === "string") {
        return curRec[part] as string;
      }
      current = curRec[part];
    } else {
      // Key not found, use fallback
      return formatKeyToLabel(key);
    }
  }

  // If we ended on an object with _label, return that
  if (current && typeof current === "object") {
    const label = (current as Record<string, unknown>)._label;
    if (typeof label === "string") return label;
  }

  // Fallback
  return formatKeyToLabel(key);
};

/**
 * Format a key to a human-readable label
 * Converts camelCase, snake_case, and kebab-case to Title Case
 * @param key - The key to format (e.g., "userProfile", "user_profile", "user-profile")
 * @returns Formatted label (e.g., "User Profile")
 */
export const formatKeyToLabel = (key: string): string => {
  if (!key) return "";

  // Handle nested keys - just use the last part
  const lastPart = key.split(".").pop() || key;

  return (
    lastPart
      // Replace underscores and hyphens with spaces
      .replace(/[_-]/g, " ")
      // Insert space before capital letters (camelCase)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // Capitalize first letter of each word
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
};

/**
 * Get all permission labels as a flat object
 * Useful for rendering permission lists
 * @returns Flat object with permission paths as keys and labels as values
 */
export const getFlatPermissionLabels = (): Record<string, string> => {
  const result: Record<string, string> = {};

  const flatten = (obj: Record<string, unknown>, prefix = "") => {
    for (const key in obj) {
      if (key === "_label") continue;

      const value = obj[key];
      const path = prefix ? `${prefix}.${key}` : key;

      if (typeof value === "string") {
        result[path] = value;
      } else if (typeof value === "object" && value !== null) {
        const valueRec = value as Record<string, unknown>;
        if (typeof valueRec._label === "string") {
          result[path] = valueRec._label;
        }
        flatten(valueRec, path);
      }
    }
  };

  flatten(permissionLabels as unknown as Record<string, unknown>);
  return result;
};

/**
 * Get permission category label
 * @param category - Category key (e.g., "attendance", "employees")
 * @returns Category label (e.g., "Attendance", "Employees")
 */
export const getPermissionCategoryLabel = (category: string): string => {
  const categoryObj = permissionLabels[category];
  if (categoryObj && typeof categoryObj === "object") {
    const label = (categoryObj as Record<string, unknown>)._label;
    if (typeof label === "string") {
      return label;
    }
  }
  return formatKeyToLabel(category);
};
